"use client";

// status: updated

import { useState, useRef, useEffect } from "react";
import { Images, Videos } from "@assets/assets";
import Image from "next/image";
import DeferredVideo from "@/components/DeferredVideo";
import { ensureGsap } from "@/lib/EnsureGsap";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { whenIdle } from "@/lib/whenIdle";

interface WhychooseConstantProps {
  title: React.ReactNode;
  subtitle: string;
  description: string;
  button: React.ReactNode;
  showLogos?: boolean;
}

export default function WhychooseConstant({
  title,
  subtitle,
  description,
  button,
  showLogos = true,
}: WhychooseConstantProps) {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const marqueeContentRef = useRef<HTMLDivElement>(null);
  const tlRef = useRef<any | null>(null);

  const [inView, setInView] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const isMobile = useIsMobile();

  /*
   * Client logos
   */
  const logos = [
    { src: Images?.logoSet.bonusway, name: "Bonusway" },
    { src: Images?.logoSet.delMonte, name: "DelMonte" },
    { src: Images?.logoSet.festivity, name: "Festivity" },
    { src: Images?.logoSet.nasscom, name: "NASSCOM" },
    { src: Images?.logoSet.paceFunding, name: "PaceFunding" },
    { src: Images?.logoSet.rawbank, name: "Rawbank" },
    { src: Images?.logoSet.rayTech, name: "RayTech" },
    { src: Images?.logoSet.unitedNations, name: "United Nations" },
    { src: Images?.logoSet.volkswagen, name: "Volkswagen" },
  ];

  /*
   * Logos marquee
   *
   * Only initialize GSAP when showLogos is true.
   */
  useEffect(() => {
    if (!showLogos) {
      tlRef.current?.kill();
      tlRef.current = null;
      return;
    }

    const content = marqueeContentRef.current;

    if (!content) return;

    let cancelled = false;

    const initMarquee = async () => {
      const { gsap } = await ensureGsap();

      if (cancelled) return;

      requestAnimationFrame(() => {
        if (cancelled || !content) return;

        const contentWidth = content.scrollWidth / 2;

        if (!contentWidth) return;

        gsap.set(content, {
          x: 0,
        });

        // Kill any previous animation before creating a new one
        tlRef.current?.kill();

        tlRef.current = gsap.to(content, {
          x: -contentWidth,
          duration: 45,
          ease: "none",
          repeat: -1,
          modifiers: {
            x: gsap.utils.unitize((x) => parseFloat(x) % contentWidth),
          },
        });
      });
    };

    /*
     * Defer marquee initialization until browser is idle.
     * This avoids unnecessary layout work during initial load.
     */
    const cancelIdle = whenIdle(initMarquee);

    return () => {
      cancelled = true;

      cancelIdle();

      tlRef.current?.kill();
      tlRef.current = null;
    };
  }, [showLogos]);

  /*
   * Section scroll animation
   */
  useEffect(() => {
    const el = sectionRef.current;

    if (!el) return;

    let ctx: any;
    let cancelled = false;

    const cancelIdle = whenIdle(async () => {
      if (cancelled) return;

      const { gsap } = await ensureGsap();

      if (cancelled) return;

      ctx = gsap.context(() => {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: el,

            // Start when the top of the section reaches
            // 90% of the viewport
            start: "top 90%",

            end: "bottom 20%",

            scrub: true,

            onEnter: () => setInView(true),

            // Uncomment if you want the video to reset
            // when scrolling back above the section.
            // onLeaveBack: () => setInView(false),
          },
        });

        tl.to(el.querySelector(".why-choose-main"), {
          y: 0,
          opacity: 1,
          ease: "power1.out",
          duration: 1,
        });
      }, el);
    });

    return () => {
      cancelled = true;

      cancelIdle();

      ctx?.revert?.();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="why-choose-constant relative z-[2] w-full overflow-hidden bg-[#000000]"
    >
      {/* =========================
          Texture Background
      ========================== */}
      <div className="texture-bg mix-blend-overlay absolute left-0 top-0 z-0 h-full w-full">
        {!isMobile && (
          <div className="absolute inset-0 z-0">
            <Image
              src={Images?.texture}
              alt="texture"
              fill
              className="object-cover mix-blend-overlay opacity-60"
              sizes="(max-width: 768px) 100vw, (max-width: 1536px) 50vw, 700px"
            />
          </div>
        )}
      </div>

      {/* =========================
          Background Blue Glow - Top Right
      ========================== */}
      <div
        className="
          absolute
          right-0
          top-0
          h-[907.05px]
          w-[907.05px]
          -translate-y-1/3
          translate-x-1/3
          rounded-full
          bg-[#225bd4]/40
          blur-[333.68px]
        "
      />

      {/* =========================
          Background Blue Glow - Bottom Left
      ========================== */}
      <div
        className="
          absolute
          bottom-0
          left-0
          h-[907.05px]
          w-[907.05px]
          -translate-x-1/3
          translate-y-1/3
          rounded-full
          bg-[#225bd4]/40
          blur-[333.68px]
        "
      />

      {/* =========================
          Main Content
      ========================== */}
      <div
        className={`
    why-choose-main
    relative
    z-10
    mx-auto
    w-[calc(100%_-_60px)]
    max-w-[5000px]
    pt-[20px]
    lg:pt-[80px]
    ${!showLogos ? "pb-[100px]" : ""}
  `}
      >
        <div className="why-choose-main-content flex gap-0">
          <div className="w-full px-0 pb-0 pt-6 lg:px-6 lg:pb-10">
            {/* Subtitle */}
            <p className="mb-0 w-full justify-center text-base font-medium uppercase tracking-tight text-white lg:mb-[30px]">
              {subtitle}
            </p>

            <div className="flex w-full flex-col gap-[20px] pb-4">
              {/* =========================
                  Top Content Row
              ========================== */}
              <div className="flex w-full flex-col items-start justify-between gap-8 2xl:flex-row">
                {/* Video Blob */}
                {inView &&
                  typeof window !== "undefined" &&
                  window.innerWidth > 1560 && (
                    <div className="flex min-w-[400px] flex-1 items-center justify-center">
                      <DeferredVideo
                        src={Videos.blob}
                        className="h-auto w-[350px] rounded-xl object-cover"
                        preload="none"
                        ariaLabel="blob video"
                      />
                    </div>
                  )}

                {/* Title + Description */}
                <div>
                  <h2 className="clash-font max-w-[18ch] text-[length:var(--section-title)] font-medium leading-[110%] text-white">
                    {title}
                  </h2>

                  <p className="max-w-[80ch] pt-[20px] text-[length:var(--hero-para)] font-normal leading-[160%] text-white/50 lg:pt-0">
                    {description}
                  </p>
                </div>

                {/* Button */}
                <div className="2xl:self-start">{button}</div>
              </div>
            </div>
          </div>
        </div>

        {/* =========================
            YouTube Video
        ========================== */}
        <div className="box-border flex w-full items-center justify-center lg:flex">
          <div className="group relative mt-10 aspect-video max-h-[730px] w-full cursor-pointer overflow-hidden border border-white/10">
            {!isVideoPlaying ? (
              <div
                className="absolute inset-0 z-10"
                onClick={() => setIsVideoPlaying(true)}
              >
                {/* Thumbnail */}
                <Image
                  src={Images?.YoutubeThumbNew}
                  alt="Video Thumbnail"
                  fill
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  className="object-cover transition-transform duration-700 group-hover:scale-105"
                />

                {/* Overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/10 transition-all group-hover:bg-black/30">
                  {/* YouTube Button */}
                  <div className="relative flex h-[48px] w-[68px] items-center justify-center rounded-[12px] bg-[#FF0000] shadow-xl transition-transform duration-300 group-hover:scale-110">
                    <div
                      className="
                        ml-1
                        h-0
                        w-0
                        border-y-[10px]
                        border-y-transparent
                        border-l-[18px]
                        border-l-white
                      "
                    />
                  </div>
                </div>
              </div>
            ) : (
              <iframe
                className="h-full w-full"
                src="https://www.youtube.com/embed/e5XW9PPsAPY?autoplay=1&rel=0"
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; web-share"
                allowFullScreen
              />
            )}
          </div>
        </div>
      </div>

      {/* =========================
          Trusted By / Logos
          Default: visible
          Hide with showLogos={false}
      ========================== */}
      {showLogos && (
        <>
          {/* Trusted By Title */}
          <p className="pt-10 text-center text-[length:var(--hero-para)] font-normal uppercase tracking-wide text-white lg:py-10 lg:pt-15">
            Trusted By
          </p>

          {/* Logos Marquee */}
          <div className="client-logos w-full overflow-hidden py-[20px] xl:pb-[70px]">
            <div
              ref={marqueeContentRef}
              className="
                flex
                max-w-[max-content]
                gap-[50px]
                whitespace-nowrap
                will-change-transform
              "
            >
              {[...logos, ...logos].map((logo, i) => (
                <div key={i} className="group relative w-[10vw] flex-shrink-0">
                  <Image
                    src={logo.src}
                    alt={logo.name}
                    width={200}
                    height={140}
                    className="mx-auto max-h-[140px] object-contain"
                    sizes="200px"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </section>
  );
}
